// Offline mutation queue for Tyler's Fabrication Dashboard employee app.
// Steel shops have poor cell reception; clock-in punches must work offline
// and sync the moment the network returns.
//
// Storage: IndexedDB (durable across reloads, app close, device sleep).
// API:
//   await queuePunch({ type: 'clock_in'|'clock_out', employee_id, job_id?,
//                      piece_mark?, ts: ISO string, lat?, lng?, note? })
//   await flushQueue(supabaseClient) → { sent, failed, remaining }
//   subscribeQueueStatus(fn) → unsubscribe; fn({ pending, online, syncing })
//
// Wire-up: components/PunchButton.jsx calls queuePunch + flushQueue.
//          components/OfflineBanner.jsx subscribes for the indicator.
//          App.jsx mounts a window.online listener that calls flushQueue.

const DB_NAME = 'tylersfab_offline'
const DB_VERSION = 1
const STORE = 'punches'

let _dbPromise = null
function openDb() {
  if (_dbPromise) return _dbPromise
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        const s = db.createObjectStore(STORE, { keyPath: 'local_id', autoIncrement: true })
        s.createIndex('by_ts', 'ts')
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return _dbPromise
}

const _listeners = new Set()
function emit() {
  countPending().then((pending) => {
    const state = { pending, online: navigator.onLine, syncing: _syncing }
    for (const l of _listeners) {
      try { l(state) } catch {}
    }
  })
}

let _syncing = false

export function subscribeQueueStatus(fn) {
  _listeners.add(fn)
  emit()
  return () => _listeners.delete(fn)
}

export async function countPending() {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).count()
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

export async function queuePunch(punch) {
  if (!punch || !punch.type || !punch.employee_id || !punch.ts) {
    throw new Error('queuePunch: requires { type, employee_id, ts }')
  }
  const db = await openDb()
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).add({ ...punch, queued_at: Date.now() })
    tx.oncomplete = resolve
    tx.onerror = () => reject(tx.error)
  })
  emit()
}

async function allPending() {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).getAll()
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function deleteLocal(localId) {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).delete(localId)
    tx.oncomplete = resolve
    tx.onerror = () => reject(tx.error)
  })
}

/**
 * Drain the queue. Each item is POSTed via the provided sender. On 2xx-ish
 * success the local row is dropped. On failure the row stays — we'll retry
 * on the next online event. Server is the source of truth for dedup (server
 * uses (employee_id, ts) uniqueness so retried sends are idempotent).
 */
export async function flushQueue(sender) {
  if (_syncing) return { sent: 0, failed: 0, remaining: await countPending(), busy: true }
  _syncing = true
  emit()
  const out = { sent: 0, failed: 0, remaining: 0 }
  try {
    const pending = await allPending()
    pending.sort((a, b) => (a.ts < b.ts ? -1 : 1))
    for (const row of pending) {
      try {
        await sender(row)
        await deleteLocal(row.local_id)
        out.sent++
      } catch (err) {
        out.failed++
        // Don't break — try the next one. They're independent punches.
        console.warn('flushQueue: send failed for', row.local_id, err)
      }
    }
    out.remaining = await countPending()
  } finally {
    _syncing = false
    emit()
  }
  return out
}

// Default sender: upsert into the `time_cards` table directly via the
// authenticated supabase client. RLS scopes writes to the caller's company
// (see migrations/20260530_fab_schema.sql). The row id is a deterministic
// idempotency key so retried sends are no-ops (upsert on PK).
//
// We resolve the caller's company_id from `account_members` on first send and
// cache it; without it RLS would reject the insert.
let _companyIdCache = null
async function resolveCompanyId(supabase, userId) {
  if (_companyIdCache) return _companyIdCache
  const { data, error } = await supabase
    .from('account_members')
    .select('company_id')
    .eq('user_id', userId)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle()
  if (error || !data?.company_id) throw new Error(error?.message || 'No company membership')
  _companyIdCache = data.company_id
  return _companyIdCache
}

export function makeSupabaseSender(supabase) {
  return async (row) => {
    const { data: sess } = await supabase.auth.getSession()
    const userId = sess?.session?.user?.id
    if (!userId) throw new Error('Not signed in — cannot sync')
    const companyId = await resolveCompanyId(supabase, userId)
    const idempotencyKey = `${row.employee_id}:${row.ts}`
    const { error } = await supabase.from('time_cards').upsert({
      id:           idempotencyKey,
      account_id:   userId,
      company_id:   companyId,
      employee_id:  row.employee_id,
      job_id:       row.job_id ?? null,
      punch_type:   row.type,
      punched_at:   row.ts,
      lat:          row.lat ?? null,
      lng:          row.lng ?? null,
      notes:        row.note ?? null,
    }, { onConflict: 'id', ignoreDuplicates: false })
    if (error) throw new Error(error.message || 'time_cards upsert failed')
  }
}

// Wire-up helper: call once at app boot.
export function installAutoFlush(supabase) {
  const sender = makeSupabaseSender(supabase)
  let scheduled = false
  function maybeFlush() {
    if (!navigator.onLine) return
    if (scheduled) return
    scheduled = true
    setTimeout(() => {
      scheduled = false
      flushQueue(sender).catch((e) => console.warn('autoflush', e))
    }, 250)
  }
  window.addEventListener('online', maybeFlush)
  window.addEventListener('focus', maybeFlush)
  maybeFlush()
  return () => {
    window.removeEventListener('online', maybeFlush)
    window.removeEventListener('focus', maybeFlush)
  }
}
